import { useEffect, useState } from "react";
import api, { fmtRM, fmtDate } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { TrendingUp, Wallet, BookOpenCheck, UserRound, Trophy } from "lucide-react";

const TIER_STYLE = {
  master_agent: "bg-gold-100 text-gold-700 border-gold-500/40",
  senior_agent: "bg-purple-50 text-purple-700 border-purple-200",
  agent: "bg-sky-50 text-sky-700 border-sky-200",
};

export default function AgentPortal() {
  const { t } = useLang();
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/agent/summary").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data) return <div className="flex justify-center h-64 items-center" data-testid="agent-portal-loading"><div className="w-8 h-8 border-4 border-brand-800 border-t-gold-500 rounded-full animate-spin" /></div>;

  const { agent, bookings, stats } = data;
  const cards = [
    { icon: TrendingUp, label: "Total Sales", value: fmtRM(stats.sales), testid: "agent-stat-sales" },
    { icon: Wallet, label: "Collected", value: fmtRM(stats.collected), testid: "agent-stat-collected" },
    { icon: Trophy, label: "Commission Earned", value: fmtRM(stats.commission), testid: "agent-stat-commission", gold: true },
    { icon: BookOpenCheck, label: "Bookings", value: stats.bookings, testid: "agent-stat-bookings" },
    { icon: UserRound, label: "Pax", value: stats.pax, testid: "agent-stat-pax" },
  ];

  return (
    <div className="space-y-5" data-testid="agent-portal-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-sans text-2xl sm:text-3xl font-bold text-slate-900 tracking-tight">{t("app.agent_portal")}</h1>
          <p className="text-sm text-muted-foreground mt-1">{agent.name} • {agent.agent_code} • {agent.branch || "HQ"}</p>
        </div>
        <span className={`inline-flex px-3 py-1.5 rounded-full border text-[10px] font-mono uppercase tracking-widest ${TIER_STYLE[agent.tier] || TIER_STYLE.agent}`} data-testid="agent-tier-badge">
          {agent.tier.replace(/_/g, " ")} • {agent.commission_rate}% comm
        </span>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        {cards.map(({ icon: Icon, label, value, testid, gold }) => (
          <div key={testid} className="pro-card p-4" data-testid={testid}>
            <div className={`w-8 h-8 rounded-lg border flex items-center justify-center ${gold ? "bg-gold-100/60 border-gold-500/25" : "bg-emerald-50 border-emerald-100"}`}>
              <Icon className={`w-4 h-4 ${gold ? "text-gold-600" : "text-brand-700"}`} />
            </div>
            <div className={`mt-3 font-sans text-lg font-bold tabular-nums ${gold ? "text-gold-700" : "text-slate-900"}`}>{value}</div>
            <div className="text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{label}</div>
          </div>
        ))}
      </div>

      <div className="bg-gold-100/50 border border-gold-500/25 rounded-xl px-4 py-3 text-xs text-gold-700" data-testid="agent-commission-note">
        Commission is verified after full payment & travel completion, then approved by finance before payout.
      </div>

      <div className="pro-card overflow-x-auto" data-testid="agent-bookings">
        <h2 className="font-sans font-semibold text-slate-900 p-5 pb-3">My Bookings</h2>
        <table className="w-full text-sm min-w-[680px]">
          <thead>
            <tr className="border-y border-sand-border text-left bg-[#F8F6F2]/80">
              {["Booking", "Customer", "Package", "Departure", "Pax", "Total", "Status"].map((h) => (
                <th key={h} className="px-5 py-2.5 text-[10px] font-mono uppercase tracking-wider text-muted-foreground">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {bookings.map((b) => (
              <tr key={b.booking_id} className="border-b border-sand-border/60 hover:bg-emerald-50/60 transition-colors" data-testid={`agent-booking-${b.booking_no}`}>
                <td className="px-5 py-3 font-mono text-xs text-brand-800">{b.booking_no}</td>
                <td className="px-5 py-3 font-medium text-slate-900">{b.customer_name}</td>
                <td className="px-5 py-3 text-xs max-w-[200px] truncate">{b.package_name}</td>
                <td className="px-5 py-3 text-xs whitespace-nowrap">{fmtDate(b.departure_date)}</td>
                <td className="px-5 py-3 font-mono text-xs">{b.pax}</td>
                <td className="px-5 py-3 font-mono text-xs">{fmtRM(b.total)}</td>
                <td className="px-5 py-3">
                  <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-mono uppercase ${b.outstanding > 0 ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-emerald-50 text-emerald-700 border-emerald-200"}`}>
                    {b.outstanding > 0 ? `${fmtRM(b.outstanding)} due` : "paid"}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {bookings.length === 0 && <div className="text-center py-10 text-muted-foreground text-sm">No bookings yet</div>}
      </div>
    </div>
  );
}
